import React, { useState, useEffect } from 'react';
import {
  IconTrash,
  IconCopy,
  IconCheck,
  IconSettings,
  IconShield,
  IconWorld,
  IconPlus,
  IconX,
  IconBan,
  IconAlertTriangle,
  IconMapPin,
  IconLoader2,
  IconPlayerPlay,
  IconSquare,
  IconLock,
  IconWorldWww,
  IconPencil
} from '@tabler/icons-react';
import { GameIcon } from '../../constants/games';
import { tunnelsAPI } from '../../api/tunnels';
import { nodesAPI } from '../../api/nodes';

const copyToClipboard = (text) => {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  // Fallback for non-HTTPS
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const TunnelCard = ({ tunnel, onDelete, onUpdateSettings, onRefresh }) => {
  const [copied, setCopied] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localStatus, setLocalStatus] = useState(null);
  
  // Настройки фаервола
  const [firewallEnabled, setFirewallEnabled] = useState(tunnel.firewall_enabled || false);
  const [allowedIPs, setAllowedIPs] = useState(tunnel.allowed_ips || []);
  const [newIP, setNewIP] = useState('');
  
  // Настройки домена
  const [customDomain, setCustomDomain] = useState(tunnel.custom_domain || '');
  const [domainVerified, setDomainVerified] = useState(tunnel.domain_verified || false);
  const [sslEnabled, setSslEnabled] = useState(tunnel.ssl_enabled || false);
  const [verifying, setVerifying] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState('');
  // Режим изменения домена (по карандашику)
  const [editingDomain, setEditingDomain] = useState(false);
  // Локально подтверждённый домен: чтобы карточка переключилась в компактный
  // вид сразу после проверки, не дожидаясь обновления props
  const [verifiedDomain, setVerifiedDomain] = useState('');
  // Домен, сохранённый в туннеле: для компактного вида и сравнения с вводом
  const savedDomain = (verifiedDomain || tunnel.custom_domain || '').trim();

  // Смена ноды
  const [showChangeNode, setShowChangeNode] = useState(false);
  const [availableNodes, setAvailableNodes] = useState([]);
  const [loadingNodes, setLoadingNodes] = useState(false);
  const [changingNode, setChangingNode] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  
  // Синхронизируем состояние с props при обновлении tunnel
  useEffect(() => {
    setFirewallEnabled(tunnel.firewall_enabled || false);
    setAllowedIPs(tunnel.allowed_ips || []);
    setCustomDomain(tunnel.custom_domain || '');
    setDomainVerified(tunnel.domain_verified || false);
    setSslEnabled(tunnel.ssl_enabled || false);
  }, [tunnel.firewall_enabled, tunnel.allowed_ips, tunnel.custom_domain, tunnel.domain_verified, tunnel.ssl_enabled]);
  
  const effectiveStatus = localStatus || tunnel.status;
  const isActive = effectiveStatus === 'active';
  const isBlocked = tunnel.is_blocked || false;
  const isAppClient = typeof window !== 'undefined' && !!window.TunnelBridge;

  useEffect(() => {
    if (tunnel.status === 'active' && localStatus === null) {
      // Сервер подтвердил active, а пользователь не нажимал IconPlayerPlay — синхронизируем
      setLocalStatus('active');
    }
  }, [tunnel.status]);

  const formatBlockedUntil = (dateStr) => {
    if (!dateStr) return 'навсегда';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const displayAddress = tunnel.publicAddress;

  const handleCopy = (e) => {
    e.stopPropagation();
    copyToClipboard(displayAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAddIP = () => {
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    if (newIP && ipRegex.test(newIP) && !allowedIPs.includes(newIP)) {
      setAllowedIPs([...allowedIPs, newIP]);
      setNewIP('');
    }
  };

  const handleRemoveIP = (ip) => {
    setAllowedIPs(allowedIPs.filter(i => i !== ip));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${API_BASE_URL}/v1/tunnels/${tunnel.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token || ''}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          firewall_enabled: firewallEnabled,
          allowed_ips: allowedIPs,
          custom_domain: customDomain,
          ssl_enabled: sslEnabled
        })
      });
      
      if (response.ok) {
        setShowSettings(false);
        if (onUpdateSettings) {
          onUpdateSettings(tunnel.id, { firewallEnabled, allowedIPs, customDomain });
        }
      } else {
        console.error('Ошибка сохранения:', await response.text());
      }
    } catch (error) {
      console.log('Ошибка сохранения настроек:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    if (!customDomain) return;
    
    setVerifying(true);
    setVerifyMessage('');
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
      const token = localStorage.getItem('access_token');
      
      // Сначала сохраняем домен если он новый
      await fetch(`${API_BASE_URL}/v1/tunnels/${tunnel.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token || ''}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ custom_domain: customDomain })
      });
      
      // Затем проверяем NS записи
      const response = await fetch(`${API_BASE_URL}/v1/tunnels/${tunnel.id}/verify-domain`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token || ''}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (data.verified) {
        setDomainVerified(true);
        setVerifiedDomain(customDomain.trim());
        setEditingDomain(false);
        setVerifyMessage('✓ Домен подтверждён! Доступ: ' + data.access_url);
      } else {
        setVerifyMessage('✗ ' + (data.message || 'NS записи не найдены'));
      }
    } catch (error) {
      setVerifyMessage('✗ Ошибка проверки');
    } finally {
      setVerifying(false);
    }
  };

  const handleOpenChangeNode = async () => {
    setShowChangeNode(true);
    setLoadingNodes(true);
    try {
      const nodes = await nodesAPI.getAvailable();
      setAvailableNodes(nodes);
    } catch (error) {
      console.error('Ошибка загрузки нод:', error);
    } finally {
      setLoadingNodes(false);
    }
  };

  const handleChangeNode = async () => {
    if (!selectedNode) return;
    
    setChangingNode(true);
    try {
      await tunnelsAPI.changeNode(tunnel.id, selectedNode);
      setShowChangeNode(false);
      setSelectedNode(null);
      if (onUpdateSettings) {
        onUpdateSettings(tunnel.id, { node_id: selectedNode });
      }
    } catch (error) {
      console.error('Ошибка смены ноды:', error);
      alert('Ошибка смены ноды: ' + error.message);
    } finally {
      setChangingNode(false);
    }
  };

  return (
    <>
      <div className={`bg-white dark:bg-dark-900 border rounded-2xl transition-all hover:border-gray-300 dark:hover:border-dark-700 ${
        isBlocked ? 'border-red-500/50' : isActive ? 'border-green-500/30' : 'border-gray-200 dark:border-dark-800'
      }`}>
        {/* Blocked Banner */}
        {isBlocked && (
          <div className="bg-red-500/10 border-b border-red-500/30 px-5 py-3 rounded-t-2xl">
            <div className="flex items-center gap-2 text-red-500">
              <IconBan className="w-4 h-4" />
              <span className="font-medium text-sm">Туннель заблокирован администратором</span>
            </div>
            <div className="text-xs text-red-400/80 mt-1">
              {tunnel.block_reason && <span>Причина: {tunnel.block_reason}. </span>}
              <span>До: {formatBlockedUntil(tunnel.blocked_until)}</span>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-4 p-5">
        {/* Info */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-500/20 to-primary-600/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <GameIcon gameId={tunnel.game} name={tunnel.name} className="w-10 h-10" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                {tunnel.name}
              </h3>
              <span className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isBlocked
                  ? 'bg-red-500/15 text-red-500'
                  : isActive 
                    ? 'bg-green-500/15 text-green-600 dark:text-green-400' 
                    : 'bg-gray-100 dark:bg-dark-800 text-gray-500 dark:text-dark-500'
              }`}>
                <span className={`w-2 h-2 rounded-full ${isBlocked ? 'bg-red-500' : isActive ? 'bg-green-400 shadow-[0_0_6px_rgba(34,197,94,0.4)]' : 'bg-dark-600'}`} />
                <span>{isBlocked ? 'Заблокирован' : isActive ? 'Активен' : 'Остановлен'}</span>
              </span>
            </div>

            <p className="text-gray-600 dark:text-dark-500 text-sm mb-2">{tunnel.gameLabel}</p>

            {/* Address */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-dark-950 border border-gray-200 dark:border-dark-800 rounded-lg max-w-md flex-1 min-w-0">
                <code className="text-primary-500 text-sm font-mono truncate flex-1">
                  {displayAddress}
                </code>
                <button
                  onClick={handleCopy}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-dark-800 rounded transition-colors flex-shrink-0"
                >
                  {copied ? (
                    <IconCheck className="w-4 h-4 text-green-400" />
                  ) : (
                    <IconCopy className="w-4 h-4 text-gray-500 dark:text-dark-500" />
                  )}
                </button>
              </div>
            </div>

            {/* Привязанный домен и статус SSL */}
            {(tunnel.customDomain || tunnel.sslEnabled) && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {tunnel.customDomain && (
                  <span
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${
                      tunnel.domainVerified
                        ? 'bg-primary-500/10 text-primary-500 border-primary-500/30'
                        : 'bg-amber-500/10 text-amber-500 border-amber-500/30'
                    }`}
                    title={tunnel.domainVerified ? 'Домен привязан и подтверждён' : 'Домен привязан, но NS ещё не делегированы'}
                  >
                    <IconWorldWww className="w-3.5 h-3.5" />
                    {tunnel.customDomain}
                    {!tunnel.domainVerified && ' · не подтверждён'}
                  </span>
                )}
                <span
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${
                    tunnel.sslEnabled
                      ? 'bg-green-500/10 text-green-500 border-green-500/30'
                      : 'bg-gray-100 dark:bg-dark-850 text-gray-500 dark:text-dark-500 border-gray-200 dark:border-dark-800'
                  }`}
                >
                  <IconLock className="w-3.5 h-3.5" />
                  SSL {tunnel.sslEnabled ? 'включён' : 'выключен'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Protocol badge */}
        <div className="hidden sm:block">
          <span className="px-3 py-1.5 bg-gray-100 dark:bg-dark-850 border border-gray-200 dark:border-dark-800 rounded-lg text-gray-600 dark:text-dark-400 text-sm font-medium">
            {tunnel.protocol || 'TCP'}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {isAppClient && effectiveStatus === 'waiting_for_client' && (
            <button
              onClick={() => {
                setLocalStatus('active');
                window.TunnelBridge.launch({
                  id: tunnel.id,
                  access_token: tunnel.access_token,
                  local_port: tunnel.local_port,
                  local_ip: tunnel.local_ip || '127.0.0.1',
                  nodes: tunnel.nodes || [],
                  public_host: tunnel.public_host || 'connect.xneon.org',
                });
              }}
              className="p-2.5 rounded-lg bg-green-500/10 border border-green-500/30 text-green-600 dark:text-green-400 hover:bg-green-500/20 transition-colors"
              title="Запустить туннель"
            >
              <IconPlayerPlay className="w-5 h-5" />
            </button>
          )}
          {isAppClient && effectiveStatus === 'active' && (
            <button
              onClick={() => {
                setLocalStatus('waiting_for_client');
                window.TunnelBridge.stop(tunnel.id);
              }}
              className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors"
              title="Остановить туннель"
            >
              <IconSquare className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => setShowSettings(true)}
            className="p-2.5 rounded-lg bg-gray-100 dark:bg-dark-850 border border-gray-200 dark:border-dark-800 text-gray-500 dark:text-dark-500 hover:text-primary-500 hover:border-primary-500/30 transition-colors"
          >
            <IconSettings className="w-5 h-5" />
          </button>
          <button
            onClick={handleOpenChangeNode}
            className="p-2.5 rounded-lg bg-gray-100 dark:bg-dark-850 border border-gray-200 dark:border-dark-800 text-gray-500 dark:text-dark-500 hover:text-blue-500 hover:border-blue-500/30 transition-colors"
            title="Сменить ноду"
          >
            <IconMapPin className="w-5 h-5" />
          </button>
          {!isBlocked && (
            <button
              onClick={onDelete}
              className="p-2.5 rounded-lg bg-gray-100 dark:bg-dark-850 border border-gray-200 dark:border-dark-800 text-gray-500 dark:text-dark-500 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/30 transition-colors"
            >
              <IconTrash className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
      </div>

      {/* IconSettings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowSettings(false)}
          />
          
          {/* Modal */}
          <div className="relative w-full max-w-lg bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-dark-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500/20 to-primary-600/10 rounded-xl flex items-center justify-center">
                  <GameIcon gameId={tunnel.game} name={tunnel.name} className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-gray-900 dark:text-white font-semibold">{tunnel.name}</h3>
                  <p className="text-gray-600 dark:text-dark-500 text-sm">Настройки туннеля</p>
                </div>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 rounded-lg text-gray-500 dark:text-dark-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
              >
                <IconX className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-5 space-y-5 max-h-[60vh] overflow-y-auto">
              {/* Firewall IconSettings */}
              <div className="bg-gray-50 dark:bg-dark-950 border border-gray-200 dark:border-dark-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-500/10 rounded-lg flex items-center justify-center">
                      <IconShield className="w-4 h-4 text-red-400" />
                    </div>
                    <div>
                      <h4 className="text-gray-900 dark:text-white font-medium text-sm">Фаервол</h4>
                      <p className="text-gray-600 dark:text-dark-500 text-xs">Ограничить доступ по IP</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setFirewallEnabled(!firewallEnabled)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      firewallEnabled ? 'bg-primary-500' : 'bg-gray-300 dark:bg-dark-700'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      firewallEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {firewallEnabled && (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newIP}
                        onChange={(e) => setNewIP(e.target.value)}
                        placeholder="192.168.1.100 или 10.0.0.0/8"
                        className="flex-1 px-3 py-2 bg-white dark:bg-dark-900 border border-gray-300 dark:border-dark-700 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500"
                        onKeyPress={(e) => e.key === 'Enter' && handleAddIP()}
                      />
                      <button
                        onClick={handleAddIP}
                        className="px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                      >
                        <IconPlus className="w-4 h-4" />
                      </button>
                    </div>

                    {allowedIPs.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {allowedIPs.map((ip, index) => (
                          <div key={index} className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-200 dark:bg-dark-800 rounded-lg">
                            <code className="text-gray-700 dark:text-dark-300 text-xs font-mono">{ip}</code>
                            <button
                              onClick={() => handleRemoveIP(ip)}
                              className="text-gray-500 dark:text-dark-500 hover:text-red-400 transition-colors"
                            >
                              <IconX className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-yellow-500 text-xs">⚠️ Добавьте IP, иначе никто не сможет подключиться</p>
                    )}
                  </div>
                )}
              </div>

              {/* Свой домен */}
              <div className="bg-gray-50 dark:bg-dark-950 border border-gray-200 dark:border-dark-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                      <IconWorldWww className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <h4 className="text-gray-900 dark:text-white font-medium text-sm">Свой домен</h4>
                      <p className="text-gray-600 dark:text-dark-500 text-xs">
                        {savedDomain && domainVerified && !editingDomain
                          ? 'Домен привязан к туннелю'
                          : 'Привязать свой домен к туннелю'}
                      </p>
                    </div>
                  </div>
                  {savedDomain && domainVerified && !editingDomain && (
                    <button
                      onClick={() => setEditingDomain(true)}
                      title="Изменить домен"
                      className="p-2 rounded-lg text-gray-500 dark:text-dark-400 hover:bg-gray-200 dark:hover:bg-dark-800 transition-colors"
                    >
                      <IconPencil className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {savedDomain && domainVerified && !editingDomain ? (
                  /* Домен привязан и подтверждён — показываем компактно, без инструкций */
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-green-500/5 border border-green-500/20 rounded-lg">
                    <IconWorldWww className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <a
                      href={`${sslEnabled ? 'https' : 'http'}://${savedDomain}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-gray-900 dark:text-white hover:text-primary-500 truncate"
                    >
                      {savedDomain}
                    </a>
                    <span className="ml-auto flex items-center gap-1 text-xs font-medium text-green-500 flex-shrink-0">
                      <IconCheck className="w-3.5 h-3.5" />
                      Подтверждён
                    </span>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={customDomain}
                      onChange={(e) => setCustomDomain(e.target.value)}
                      placeholder="mydomain.com"
                      className="w-full px-3 py-2 bg-white dark:bg-dark-900 border border-gray-300 dark:border-dark-700 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500 mb-3"
                    />

                    {/* Инструкции для регистратора нужны только пока домен не подтверждён
                        или пока введён новый (другой) домен */}
                    {customDomain && (!domainVerified || customDomain !== savedDomain) && (
                      <div className="p-3 bg-gray-100 dark:bg-dark-900 rounded-lg text-xs space-y-2">
                        <p className="text-gray-700 dark:text-dark-300 font-medium">Настройте NS записи у регистратора:</p>
                        <div className="space-y-1 text-gray-600 dark:text-dark-400">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">NS 1:</span>
                            <code className="text-primary-500 bg-primary-500/10 px-2 py-0.5 rounded">ns1.xneon.org</code>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">NS 2:</span>
                            <code className="text-primary-500 bg-primary-500/10 px-2 py-0.5 rounded">ns2.xneon.org</code>
                          </div>
                        </div>

                        <button
                          onClick={handleVerifyDomain}
                          disabled={verifying}
                          className="w-full mt-3 px-3 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-400 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          {verifying ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Проверка...
                            </>
                          ) : (
                            'Проверить NS записи'
                          )}
                        </button>

                        {verifyMessage && (
                          <p className={`text-xs mt-2 ${verifyMessage.startsWith('✓') ? 'text-green-500' : 'text-red-400'}`}>
                            {verifyMessage}
                          </p>
                        )}
                      </div>
                    )}

                    {editingDomain && (
                      <button
                        onClick={() => {
                          setCustomDomain(savedDomain);
                          setEditingDomain(false);
                          setVerifyMessage('');
                        }}
                        className="w-full mt-3 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        Отмена
                      </button>
                    )}
                  </>
                )}
              </div>

            {/* SSL IconSettings */}
            <div className="bg-gray-50 dark:bg-dark-950 border border-gray-200 dark:border-dark-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                    <IconLock className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <h4 className="text-gray-900 dark:text-white font-medium text-sm">SSL (HTTPS)</h4>
                    <p className="text-gray-600 dark:text-dark-500 text-xs">Шифрованное подключение к туннелю</p>
                  </div>
                </div>
                <button
                  onClick={() => setSslEnabled(!sslEnabled)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    sslEnabled ? 'bg-primary-500' : 'bg-gray-300 dark:bg-dark-700'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    sslEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              {sslEnabled && (
                <p className="text-xs text-green-500">HTTPS будет включён для вашего туннеля</p>
              )}
            </div>
          </div>

            {/* Footer */}
            <div className="flex gap-3 p-5 border-t border-gray-200 dark:border-dark-800">
              <button
                onClick={() => setShowSettings(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-dark-800 text-gray-700 dark:text-dark-300 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-700 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={() => {
                  handleSaveSettings();
                  setShowSettings(false);
                }}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <IconCheck className="w-4 h-4" />
                )}
                <span>{saving ? 'Сохранение...' : 'Сохранить'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Node Modal */}
      {showChangeNode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowChangeNode(false)}
          />
          
          {/* Modal */}
          <div className="relative w-full max-w-lg bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-dark-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center">
                  <IconMapPin className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-gray-900 dark:text-white font-semibold">Сменить ноду</h3>
                  <p className="text-gray-600 dark:text-dark-500 text-sm">Выберите новую ноду для туннеля</p>
                </div>
              </div>
              <button
                onClick={() => setShowChangeNode(false)}
                className="p-2 rounded-lg text-gray-500 dark:text-dark-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
              >
                <IconX className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-5 max-h-[60vh] overflow-y-auto">
              {loadingNodes ? (
                <div className="flex items-center justify-center py-8">
                  <IconLoader2 className="w-6 h-6 text-primary-500 animate-spin" />
                  <span className="ml-2 text-gray-600 dark:text-dark-500">Загрузка нод...</span>
                </div>
              ) : availableNodes.length === 0 ? (
                <div className="text-center py-8">
                  <IconAlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                  <p className="text-gray-600 dark:text-dark-500">Нет доступных нод</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableNodes.map((node) => (
                    <button
                      key={node.id}
                      onClick={() => setSelectedNode(node.id)}
                      className={`w-full p-4 border rounded-xl text-left transition-all ${
                        selectedNode === node.id
                          ? 'border-blue-500 bg-blue-500/5'
                          : 'border-gray-200 dark:border-dark-800 hover:border-gray-300 dark:hover:border-dark-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{node.flag}</span>
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white">{node.name}</h4>
                            <p className="text-sm text-gray-600 dark:text-dark-500">{node.location}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-xs font-medium ${
                            node.status === 'online' ? 'text-green-500' : 'text-red-500'
                          }`}>
                            {node.status === 'online' ? 'Онлайн' : 'Офлайн'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-dark-500">
                            {node.active_tunnels || 0} активных · {node.total_tunnels || 0} всего
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-5 border-t border-gray-200 dark:border-dark-800">
              <button
                onClick={() => setShowChangeNode(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-dark-800 text-gray-700 dark:text-dark-300 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-700 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleChangeNode}
                disabled={!selectedNode || changingNode}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
              >
                {changingNode ? (
                  <>
                    <IconLoader2 className="w-4 h-4 animate-spin" />
                    <span>Смена...</span>
                  </>
                ) : (
                  <>
                    <IconMapPin className="w-4 h-4" />
                    <span>Сменить ноду</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TunnelCard;
